/**
 *
 * ReportPeriodicityList
 *
 */
import { useState } from 'react';

import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, HookFormResolver } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { type ColDef } from 'ag-grid-community';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { UtilsDates } from '../../../../../../../utils';
import { ActionsGrid } from '../../../../../../components/grid';
import { type ActionColumn } from '../../../../../../components/grid/actions-column';
import GridApplication from '../../../../../../components/grid/gridApplication';
import { usePeriodicityTypes } from '../../../services/hooks';
import { BaseUrlReportPeriodicity, BaseUrlReports } from '../../../services/http';
import { headers } from '../../../translation';
import { type ReportPeriodicity } from '../types';

interface Props {
  reportId: string;
}

export function ReportPeriodicityList(props: Props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  return <ListOfPeriodicities reportId={props.reportId} />;
}

function ListOfPeriodicities(props: Props) {
  const [search, setSearch] = useState({});
  const { t } = useTranslation();
  const { reportId } = props;

  const [{ loading, data: periodicityTypes }] = usePeriodicityTypes();

  const navigate = useNavigate();
  const location = useLocation();
  const url = location.pathname;

  const gotToEdit = (id: string) => {
    navigate(`/taxes/manage/complementary/${reportId}/periodicity/${id}?disabled=1`);
  };

  const onSearch = (values: any) => {
    setSearch({ periodicityType: values?.periodicityType?.id });
  };

  const actions: Array<ActionColumn> = [
    {
      icon: Icon.PencilSquare,
      variant: 'primary',
      onClick: (row: ReportPeriodicity) => {
        gotToEdit(row.id as string);
      }
    }
  ];
  const colDef: Array<ColDef> = [
    {
      headerName: headers(t, 'actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },
    {
      headerName: headers(t, 'periodicityType'),
      field: 'periodicityType',
      valueFormatter: (params) => params.value.name
    },
    { headerName: headers(t, 'filingDueDay'), field: 'filingDueDay' },
    {
      headerName: headers(t, 'dateFrom'),
      field: 'dateFrom',
      valueFormatter: (params) => UtilsDates.toString(params.value as Date)
    },
    {
      headerName: headers(t, 'dateTo'),
      field: 'dateTo',
      valueFormatter: (params) => (params.value ? UtilsDates.toString(params.value as Date) : '')
    }
  ];

  const newReportPeriodicity = () => {
    navigate(`${url}/create?disabled=1`);
  };

  return (
    <>
      <HookFormResolver onSubmit={onSearch} initialValues={{ periodicityType: null }}>
        {({ reset }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Combobox
                    data={!loading ? periodicityTypes : []}
                    code={'code'}
                    display={'name'}
                    label={headers(t, 'periodicityType')}
                    name="periodicityType"
                  />
                </Col>
                <Col />
              </Row>

              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    type={'reset'}
                    onClick={() => {
                      setSearch({});
                      reset();
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.new} onClick={newReportPeriodicity} />
                  <Button action={ButtonAction.find} type={'submit'} variant={'primary'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
      <hr />
      <Row>
        <Col>
          <GridApplication
            url={`${BaseUrlReports}/${props.reportId}${BaseUrlReportPeriodicity}`}
            columnDefs={colDef}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
}
